import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useForm, useFieldArray, Controller } from 'react-hook-form';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Autocomplete,
  FormControlLabel,
  Checkbox,
  IconButton,
  Snackbar,
  Alert,
  CircularProgress,
  Collapse,
  Tooltip,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import SaveIcon from '@mui/icons-material/Save';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import {
  useOrderTemplate,
  useCreateOrderTemplate,
  useUpdateOrderTemplate,
  useOrderScheduleCustomers,
  useOrderScheduleTeams,
  useOrderScheduleLabels,
  useOrderScheduleProducts,
} from '../../hooks/useApi';
import type { OrderTemplateInput, StopTemplate, InvoiceLineTemplate } from '../../types';
import PlacesAutocomplete, { type PlaceResult } from '../../components/common/PlacesAutocomplete';

const cargoTypes = [
  { value: 'PALLET_GOODS', label: 'Pallegods' },
  { value: 'GENERAL_CARGO', label: 'Stykgods' },
  { value: 'COOL', label: 'Køl' },
  { value: 'FREEZE', label: 'Frost' },
  { value: 'BULK_CARGO', label: 'Bulk' },
  { value: 'CONTAINER', label: 'Container' },
  { value: 'MACHINES', label: 'Maskiner' },
  { value: 'PACKAGES', label: 'Pakker' },
  { value: 'MISCELLANEOUS', label: 'Diverse' },
  { value: 'DELIVERY', label: 'Levering' },
  { value: 'COLLECTION', label: 'Afhentning' },
  { value: 'SERVICE', label: 'Service' },
  { value: 'WASTE', label: 'Affald' },
];

interface StopFormData {
  dateOffset: number;
  time: string;
  openingTime: string;
  loadmeters: string;
  weight: string;
  height: string;
  width: string;
  length: string;
  cubicMeters: string;
  ref: string;
  contact: string;
  contactPhone: string;
  tag: string;
  placeName: string;
  address: string;
  postcode: string;
  city: string;
  country: string;
}

interface InvoiceLineFormData {
  description: string;
  amount: string;
  quantity: string;
  unitName: string;
  productId: string;
  productNumber: string;
  discountPercentage: string;
}

interface TemplateFormData {
  name: string;
  startDate: string;
  customerNumber: string;
  customerName: string;
  cargoType: string;
  description: string;
  description2: string;
  note: string;
  invoiceRef: string;
  adr: boolean;
  adrText: string;
  teamId: string;
  labelId: string;
  addresses: StopFormData[];
  invoiceLines: InvoiceLineFormData[];
}

const emptyStop: StopFormData = {
  dateOffset: 0, time: '', openingTime: '', loadmeters: '', weight: '', height: '', width: '',
  length: '', cubicMeters: '', ref: '', contact: '', contactPhone: '', tag: '',
  placeName: '', address: '', postcode: '', city: '', country: 'DK',
};

const emptyInvoiceLine: InvoiceLineFormData = {
  description: '', amount: '', quantity: '1', unitName: '', productId: '', productNumber: '', discountPercentage: '',
};

function toFormStop(t: StopTemplate): StopFormData {
  return {
    dateOffset: t.dateOffset ?? 0, time: t.time || '', openingTime: t.openingTime || '',
    loadmeters: t.loadmeters || '', weight: t.weight || '', height: t.height || '',
    width: t.width || '', length: t.length || '', cubicMeters: t.cubicMeters || '',
    ref: t.ref || '', contact: t.contact || '', contactPhone: t.contactPhone || '', tag: t.tag || '',
    placeName: t.place?.placeName || '', address: t.place?.address || '',
    postcode: t.place?.postcode || '', city: t.place?.city || '', country: t.place?.country || 'DK',
  };
}

function fromFormStop(s: StopFormData): StopTemplate {
  return {
    dateOffset: s.dateOffset,
    time: s.time || null,
    openingTime: s.openingTime || null,
    loadmeters: s.loadmeters || '0',
    weight: s.weight || null,
    height: s.height || null,
    width: s.width || null,
    length: s.length || null,
    cubicMeters: s.cubicMeters || null,
    ref: s.ref || null,
    contact: s.contact || null,
    contactPhone: s.contactPhone || null,
    tag: s.tag || null,
    place: {
      placeName: s.placeName || null,
      address: s.address || '',
      postcode: s.postcode || '',
      city: s.city || null,
      country: s.country || 'DK',
    },
  };
}

function fromFormInvoiceLine(l: InvoiceLineFormData): InvoiceLineTemplate {
  return {
    description: l.description,
    amount: l.amount,
    quantity: l.quantity || '1',
    unitName: l.unitName || null,
    productId: l.productId || null,
    productNumber: l.productNumber || null,
    discountPercentage: l.discountPercentage || null,
  };
}

function StopRow({
  prefix, index, register, setValue, watch, onRemove, canRemove,
}: {
  prefix: `addresses.${number}`;
  index: number;
  register: ReturnType<typeof useForm<TemplateFormData>>['register'];
  setValue: ReturnType<typeof useForm<TemplateFormData>>['setValue'];
  watch: ReturnType<typeof useForm<TemplateFormData>>['watch'];
  onRemove: () => void;
  canRemove: boolean;
}) {
  const [expanded, setExpanded] = useState(false);
  const currentAddress = watch(`${prefix}.address`);
  const currentCity = watch(`${prefix}.city`);
  const currentPostcode = watch(`${prefix}.postcode`);

  const placeValue: PlaceResult | null = currentAddress ? {
    address: currentAddress,
    city: currentCity || '',
    postcode: currentPostcode || '',
    country: watch(`${prefix}.country`) || 'DK',
    formatted: [currentAddress, currentPostcode, currentCity].filter(Boolean).join(', '),
  } : null;

  const handlePlaceSelect = (place: PlaceResult | null) => {
    if (place) {
      setValue(`${prefix}.address`, place.address);
      setValue(`${prefix}.postcode`, place.postcode);
      setValue(`${prefix}.city`, place.city);
      setValue(`${prefix}.country`, place.country || 'DK');
      if (!watch(`${prefix}.placeName`) && place.formatted) {
        setValue(`${prefix}.placeName`, place.formatted.split(',')[0] || '');
      }
    } else {
      setValue(`${prefix}.address`, '');
      setValue(`${prefix}.postcode`, '');
      setValue(`${prefix}.city`, '');
    }
  };

  return (
    <Box sx={{ mb: 1, borderLeft: '3px solid', borderColor: 'primary.light', pl: 1.5, py: 0.5 }}>
      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
        <Typography variant="caption" fontWeight={700} sx={{ minWidth: 60 }}>Adresse {index + 1}</Typography>
        <Box sx={{ flex: 2, minWidth: 180 }}>
          <PlacesAutocomplete
            label=""
            placeholder="Søg adresse / virksomhed..."
            value={placeValue}
            onChange={handlePlaceSelect}
          />
        </Box>
        <TextField label="Postnr" sx={{ width: 70 }} {...register(`${prefix}.postcode`)} />
        <TextField label="By" sx={{ flex: 1, minWidth: 80 }} {...register(`${prefix}.city`)} />
        <TextField label="Tid" placeholder="08:00 - 12:00" sx={{ width: 130 }} {...register(`${prefix}.time`)} />
        <Tooltip title="Flere felter">
          <IconButton size="small" onClick={() => setExpanded(!expanded)}>
            {expanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
          </IconButton>
        </Tooltip>
        {canRemove && (
          <IconButton size="small" color="error" onClick={onRemove}><DeleteIcon fontSize="small" /></IconButton>
        )}
      </Box>
      <Collapse in={expanded}>
        <Box sx={{ display: 'flex', gap: 1, mt: 1, flexWrap: 'wrap' }}>
          <TextField label="Land" sx={{ width: 60 }} {...register(`${prefix}.country`)} />
          <TextField label="Stedsnavn" sx={{ flex: 1, minWidth: 100 }} {...register(`${prefix}.placeName`)} />
          <TextField label="LDM" sx={{ width: 70 }} {...register(`${prefix}.loadmeters`)} />
          <TextField label="Vægt (kg)" sx={{ width: 80 }} {...register(`${prefix}.weight`)} />
          <TextField label="Højde (m)" sx={{ width: 80 }} {...register(`${prefix}.height`)} />
          <TextField label="Bredde (m)" sx={{ width: 80 }} {...register(`${prefix}.width`)} />
          <TextField label="Længde (m)" sx={{ width: 80 }} {...register(`${prefix}.length`)} />
          <TextField label="m³" sx={{ width: 70 }} {...register(`${prefix}.cubicMeters`)} />
          <TextField label="Ref" sx={{ flex: 1, minWidth: 80 }} {...register(`${prefix}.ref`)} />
          <TextField label="Kontakt" sx={{ flex: 1, minWidth: 100 }} {...register(`${prefix}.contact`)} />
          <TextField label="Kontakt tlf." sx={{ width: 110 }} {...register(`${prefix}.contactPhone`)} />
          <TextField label="Åbningstid" placeholder="08:00 - 16:00" sx={{ width: 130 }} {...register(`${prefix}.openingTime`)} />
          <TextField label="Mærke" sx={{ width: 100 }} {...register(`${prefix}.tag`)} />
        </Box>
      </Collapse>
    </Box>
  );
}

export default function OrderTemplateForm() {
  const { scheduleId, templateId } = useParams<{ scheduleId: string; templateId: string }>();
  const isEdit = !!templateId;
  const navigate = useNavigate();

  const { data: existingTemplate, isLoading: loadingTemplate } = useOrderTemplate(scheduleId || '', templateId || '');
  const createTemplate = useCreateOrderTemplate();
  const updateTemplate = useUpdateOrderTemplate();
  const { data: customers = [] } = useOrderScheduleCustomers();
  const { data: teams = [] } = useOrderScheduleTeams();
  const { data: labels = [] } = useOrderScheduleLabels();
  const { data: productsData } = useOrderScheduleProducts();
  const products = productsData?.products || [];

  const [showAdvanced, setShowAdvanced] = useState(false);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false, message: '', severity: 'success',
  });

  const { register, handleSubmit, control, reset, watch, setValue } = useForm<TemplateFormData>({
    defaultValues: {
      name: '', startDate: '', customerNumber: '', customerName: '',
      cargoType: '', description: '', description2: '', note: '', invoiceRef: '',
      adr: false, adrText: '', teamId: '', labelId: '',
      addresses: [{ ...emptyStop }],
      invoiceLines: [],
    },
  });

  const addressArray = useFieldArray({ control, name: 'addresses' });
  const invoiceLineArray = useFieldArray({ control, name: 'invoiceLines' });
  const watchAdr = watch('adr');
  const isPending = createTemplate.isPending || updateTemplate.isPending;

  useEffect(() => {
    if (isEdit && existingTemplate) {
      const t = existingTemplate;
      // Merge pickupTemplate + dropoffTemplate into single addresses array
      const pickups = t.pickupTemplate?.length ? t.pickupTemplate.map(toFormStop) : [];
      const dropoffs = t.dropoffTemplate?.length ? t.dropoffTemplate.map(toFormStop) : [];
      const allAddresses = [...pickups, ...dropoffs];
      reset({
        name: t.name || '',
        startDate: t.startDate ? t.startDate.split('T')[0] : '',
        customerNumber: t.customerNumber || '',
        customerName: t.customerName || '',
        cargoType: t.cargoType || '',
        description: t.description || '',
        description2: t.description2 || '',
        note: t.note || '',
        invoiceRef: t.invoiceRef || '',
        adr: t.adr,
        adrText: t.adrText || '',
        teamId: t.teamId || '',
        labelId: t.labelId || '',
        addresses: allAddresses.length ? allAddresses : [{ ...emptyStop }],
        invoiceLines: t.invoiceLines?.length ? t.invoiceLines.map((l: any) => ({
          description: l.description, amount: l.amount, quantity: l.quantity,
          unitName: l.unitName || '', productId: l.productId || '',
          productNumber: l.productNumber || '', discountPercentage: l.discountPercentage || '',
        })) : [],
      });
    }
  }, [isEdit, existingTemplate, reset]);

  async function onSubmit(data: TemplateFormData) {
    // First address = pickup, rest = dropoffs
    const allStops = data.addresses.map(fromFormStop);
    const pickupTemplate = allStops.slice(0, 1);
    const dropoffTemplate = allStops.slice(1);

    const payload: OrderTemplateInput = {
      name: data.name,
      startDate: data.startDate || null,
      customerNumber: data.customerNumber || null,
      customerName: data.customerName || null,
      cargoType: data.cargoType || null,
      description: data.description || null,
      description2: data.description2 || null,
      note: data.note || null,
      invoiceRef: data.invoiceRef || null,
      adr: data.adr,
      adrText: data.adrText || null,
      teamId: data.teamId || null,
      labelId: data.labelId || null,
      pickupTemplate,
      dropoffTemplate,
      invoiceLines: data.invoiceLines.filter(l => l.description).map(fromFormInvoiceLine),
    };

    try {
      if (isEdit) {
        await updateTemplate.mutateAsync({ scheduleId: scheduleId!, templateId: templateId!, data: payload });
        setSnackbar({ open: true, message: 'Ordreskabelon opdateret', severity: 'success' });
      } else {
        await createTemplate.mutateAsync({ scheduleId: scheduleId!, data: payload });
        setSnackbar({ open: true, message: 'Ordreskabelon oprettet', severity: 'success' });
      }
      navigate(`/ordreskemaer/${scheduleId}`);
    } catch (err: any) {
      setSnackbar({ open: true, message: err?.response?.data?.error || 'Fejl ved gemning', severity: 'error' });
    }
  }

  if (isEdit && loadingTemplate) return <Box sx={{ p: 3 }}><CircularProgress /></Box>;

  return (
    <Box component="form" onSubmit={handleSubmit(onSubmit)}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
        <Button size="small" startIcon={<ArrowBackIcon />} onClick={() => navigate(`/ordreskemaer/${scheduleId}`)}>Tilbage</Button>
        <Typography variant="h5" fontWeight={600}>{isEdit ? 'Rediger ordreskabelon' : 'Ny ordreskabelon'}</Typography>
        <Box sx={{ flex: 1 }} />
        <Button type="submit" variant="contained" startIcon={<SaveIcon />} disabled={isPending}>
          {isPending ? 'Gemmer...' : isEdit ? 'Gem ændringer' : 'Opret'}
        </Button>
      </Box>

      {/* Template info */}
      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>Ordre-skabelon</Typography>
          <Grid container spacing={1.5}>
            <Grid item xs={12} sm={3}>
              <TextField fullWidth label="Skabelonnavn" {...register('name')} />
            </Grid>
            <Grid item xs={6} sm={2}>
              <TextField fullWidth label="Startdato *" type="date" InputLabelProps={{ shrink: true }}
                {...register('startDate', { required: true })}
                helperText="Fra denne dato" />
            </Grid>
            <Grid item xs={12} sm={4}>
              <Controller
                name="customerName"
                control={control}
                render={({ field }) => (
                  <Autocomplete
                    freeSolo
                    options={customers}
                    getOptionLabel={(o) => typeof o === 'string' ? o : `${o.client} (${o.clientNumber})`}
                    value={field.value || ''}
                    onInputChange={(_, val) => field.onChange(val)}
                    onChange={(_, val) => {
                      if (val && typeof val !== 'string') {
                        field.onChange(val.client);
                        setValue('customerNumber', val.clientNumber);
                      }
                    }}
                    renderInput={(params) => <TextField {...params} label="Kunde" />}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField fullWidth label="Kundenummer" {...register('customerNumber')} />
            </Grid>

            <Grid item xs={12} sm={3}>
              <Controller
                name="cargoType"
                control={control}
                render={({ field }) => (
                  <FormControl fullWidth>
                    <InputLabel>Godstype</InputLabel>
                    <Select label="Godstype" {...field}>
                      <MenuItem value="">{'\u2014'}</MenuItem>
                      {cargoTypes.map(t => <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>)}
                    </Select>
                  </FormControl>
                )}
              />
            </Grid>
            <Grid item xs={12} sm={3}>
              <TextField fullWidth label="Godsbeskrivelse" {...register('description')} />
            </Grid>
            <Grid item xs={12} sm={3}>
              <Controller
                name="teamId"
                control={control}
                render={({ field }) => (
                  <FormControl fullWidth>
                    <InputLabel>Team</InputLabel>
                    <Select label="Team" {...field}>
                      <MenuItem value="">{'\u2014'}</MenuItem>
                      {teams.map((t: any) => <MenuItem key={t.id} value={t.id}>{t.name}</MenuItem>)}
                    </Select>
                  </FormControl>
                )}
              />
            </Grid>
            <Grid item xs={12} sm={3}>
              <Controller
                name="labelId"
                control={control}
                render={({ field }) => (
                  <FormControl fullWidth>
                    <InputLabel>Label</InputLabel>
                    <Select label="Label" {...field}>
                      <MenuItem value="">Intet label</MenuItem>
                      {labels.map((l: any) => (
                        <MenuItem key={l.id} value={l.id}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: l.color }} />
                            {l.description}
                          </Box>
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}
              />
            </Grid>
            <Grid item xs={12} sm={3}>
              <TextField fullWidth label="Fakturaref." {...register('invoiceRef')} />
            </Grid>
          </Grid>

          <Box sx={{ mt: 1 }}>
            <Button size="small" onClick={() => setShowAdvanced(!showAdvanced)}>
              {showAdvanced ? 'Skjul avanceret' : 'Vis avanceret (ADR, note, m.m.)'}
            </Button>
          </Box>
          <Collapse in={showAdvanced}>
            <Grid container spacing={1.5} sx={{ mt: 0.5 }}>
              <Grid item xs={12} sm={3}>
                <TextField fullWidth label="Beskrivelse 2" {...register('description2')} />
              </Grid>
              <Grid item xs={12} sm={3}>
                <FormControlLabel control={<Checkbox {...register('adr')} checked={watchAdr} />} label="ADR" />
              </Grid>
              {watchAdr && (
                <Grid item xs={12} sm={3}>
                  <TextField fullWidth label="ADR tekst / UN-nummer" {...register('adrText')} />
                </Grid>
              )}
              <Grid item xs={12} sm={6}>
                <TextField fullWidth label="Intern note" multiline minRows={2} {...register('note')} />
              </Grid>
            </Grid>
          </Collapse>
        </CardContent>
      </Card>

      {/* Addresses — first = pickup in DORA, rest = dropoffs */}
      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>Adresser</Typography>
          {addressArray.fields.map((field, i) => (
            <StopRow
              key={field.id}
              prefix={`addresses.${i}`}
              index={i}
              register={register}
              setValue={setValue}
              watch={watch}
              onRemove={() => addressArray.remove(i)}
              canRemove={i > 0}
            />
          ))}
          <Button size="small" startIcon={<AddIcon />} onClick={() => addressArray.append({ ...emptyStop })}>
            Tilføj adresse
          </Button>
        </CardContent>
      </Card>

      {/* Invoice lines */}
      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>Faste varelinjer</Typography>
          {invoiceLineArray.fields.map((field, i) => (
            <Box key={field.id} sx={{ display: 'flex', gap: 1, mb: 1, alignItems: 'center', flexWrap: 'wrap' }}>
              <Typography variant="caption" fontWeight={700} sx={{ minWidth: 16 }}>{i + 1}</Typography>
              <TextField label="Beskrivelse" sx={{ flex: 2, minWidth: 140 }} {...register(`invoiceLines.${i}.description`)} />
              <TextField label="Beløb" sx={{ width: 90 }} {...register(`invoiceLines.${i}.amount`)} />
              <TextField label="Antal" sx={{ width: 70 }} {...register(`invoiceLines.${i}.quantity`)} />
              <TextField label="Enhed" sx={{ width: 70 }} {...register(`invoiceLines.${i}.unitName`)} />
              <Controller
                name={`invoiceLines.${i}.productId`}
                control={control}
                render={({ field: prodField }) => (
                  <Autocomplete
                    size="small"
                    sx={{ width: 200 }}
                    options={products}
                    getOptionLabel={(o) => `${o.productNumber} — ${o.name}`}
                    value={products.find(p => p.id === prodField.value) || null}
                    onChange={(_e, val) => {
                      prodField.onChange(val?.id || '');
                      setValue(`invoiceLines.${i}.productNumber`, val?.productNumber || '');
                      if (val?.salesPrice && !watch(`invoiceLines.${i}.amount`)) {
                        setValue(`invoiceLines.${i}.amount`, val.salesPrice);
                      }
                    }}
                    renderInput={(params) => <TextField {...params} label="Produkt" />}
                    isOptionEqualToValue={(o, v) => o.id === v.id}
                  />
                )}
              />
              <TextField label="Rabat %" sx={{ width: 70 }} {...register(`invoiceLines.${i}.discountPercentage`)} />
              <IconButton size="small" color="error" onClick={() => invoiceLineArray.remove(i)}>
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Box>
          ))}
          <Button size="small" startIcon={<AddIcon />} onClick={() => invoiceLineArray.append({ ...emptyInvoiceLine })}>
            Tilføj varelinje
          </Button>
        </CardContent>
      </Card>

      <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={() => setSnackbar(s => ({ ...s, open: false }))}>
        <Alert severity={snackbar.severity} onClose={() => setSnackbar(s => ({ ...s, open: false }))}>{snackbar.message}</Alert>
      </Snackbar>
    </Box>
  );
}
